import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Menu, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { FileTree } from './FileTree';
import { MonacoEditor } from './MonacoEditor';
import { EditorToolbar } from './EditorToolbar';
import { VersionHistory } from './VersionHistory';
import { ValidationPanel } from './ValidationPanel';
import { DiffViewer } from './DiffViewer';
import { TabBar } from './TabBar';
import { contentApi } from '@/services/contentApi';
import { useValidation } from '@/hooks/useValidation';
import { cn } from '@/lib/utils';
import { EditorTabsProvider, useEditorTabs } from '@/contexts/EditorTabsContext';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { EditorAction } from '@/services/globalAssistantApi';

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
  published_version_id?: string;
  versions?: Version[];
}

interface Version {
  id: string;
  version_number: number;
  created_at?: string;
  commit_message?: string;
  is_draft?: boolean;
}

interface ValidationError {
  line: number;
  column?: number;
  message: string;
  severity?: string;
  source?: string;
}

const DRAWER_WIDTH = 280;
const VERSION_PANEL_WIDTH = 300;

// Inner component that uses the tabs context
function EditorPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Tabs context
  const {
    tabs,
    activeTabId,
    activeTab,
    openTab,
    openVersionTab,
    closeTab,
    setActiveTab,
    updateTabContent,
    saveTab,
    markTabSaved,
    hasUnsavedChanges: hasAnyUnsavedChanges,
  } = useEditorTabs();

  // Global assistant for context registration and editor actions
  const {
    registerPageContext,
    isOpen: assistantPanelOpen,
    togglePanel: toggleAssistantPanel,
    panelWidth: assistantPanelWidth,
    registerEditorActionHandler,
  } = useGlobalAssistant();

  // UI State
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [versionPanelOpen, setVersionPanelOpen] = useState(false);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // All available content items for assistant
  const [allContentItems, setAllContentItems] = useState<ContentItem[]>([]);

  // Version state (per-tab in future, for now tied to active tab)
  const [versions, setVersions] = useState<Version[]>([]);
  const [serverValidationErrors, setServerValidationErrors] = useState<ValidationError[]>([]);

  // Get content from active tab
  const content = activeTab?.content || '';
  const hasUnsavedChanges = activeTab?.isDirty || false;

  // Build selectedItem from activeTab for compatibility
  const selectedItem: ContentItem | null = activeTab
    ? {
        id: activeTab.id,
        name: activeTab.name,
        content_type: activeTab.contentType,
        published_version_id: undefined, // Loaded separately
      }
    : null;

  // Browser-side validation
  const {
    errors: browserValidationErrors,
    isValidating: isBrowserValidating,
    pyodideReady,
    pyodideLoading,
    pyodideError,
  } = useValidation(content, activeTab?.contentType, !!activeTab);

  // Combined validation errors
  const validationErrors = [...browserValidationErrors, ...serverValidationErrors];

  // Keep a ref to current browser errors
  const browserErrorsRef = useRef(browserValidationErrors);
  browserErrorsRef.current = browserValidationErrors;

  // Diff State
  const [diffOriginal, setDiffOriginal] = useState('');
  const [diffModified, setDiffModified] = useState('');
  const [diffTitle, setDiffTitle] = useState('');

  // Track published version for active tab
  const [publishedVersionId, setPublishedVersionId] = useState<string | null>(null);

  // Track if we're updating URL programmatically to avoid loops
  const isUpdatingUrl = useRef(false);

  // Load item from URL params (only when URL changes externally)
  useEffect(() => {
    // Skip if we're the ones updating the URL
    if (isUpdatingUrl.current) {
      isUpdatingUrl.current = false;
      return;
    }

    const itemId = searchParams.get('item');
    if (itemId && itemId !== activeTabId) {
      openTab(itemId).catch((error) => {
        console.error('Failed to open tab from URL:', error);
        toast({
          title: 'Error',
          description: 'Failed to load content',
          variant: 'destructive',
        });
      });
    }
  }, [searchParams, openTab, toast]); // Removed activeTabId - only react to URL changes

  // Update URL when active tab changes
  useEffect(() => {
    if (activeTabId) {
      const currentItem = searchParams.get('item');
      if (currentItem !== activeTabId) {
        isUpdatingUrl.current = true;
        setSearchParams({ item: activeTabId }, { replace: true });
      }
    }
  }, [activeTabId, setSearchParams]); // Removed searchParams to prevent loops

  // Load versions when active tab changes
  useEffect(() => {
    if (activeTabId) {
      loadVersions(activeTabId);
    }
  }, [activeTabId]);

  // Load all content items for assistant context
  useEffect(() => {
    async function loadAllItems() {
      try {
        const types = ['agent', 'skill', 'prompt', 'judge'];
        const results = await Promise.all(
          types.map(type => contentApi.listItems({ contentType: type, limit: 500 }))
        );
        setAllContentItems(results.flat());
      } catch (error) {
        console.error('Failed to load all content items:', error);
      }
    }
    loadAllItems();
  }, []);

  // Register editor context with global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'editor',
      editor: {
        file_content: content || undefined,
        file_type: activeTab?.contentType || undefined,
        validation_errors: validationErrors.map((e) => ({
          line: e.line,
          message: e.message,
          severity: e.severity,
        })),
        open_files: tabs.map((tab) => ({
          id: tab.id,
          name: tab.name,
          type: tab.contentType,
          content: tab.content,
          is_focused: tab.id === activeTabId,
        })),
        available_files: allContentItems.map((item) => ({
          id: item.id,
          name: item.name,
          content_type: item.content_type,
        })),
      },
    });
  }, [registerPageContext, content, activeTab, validationErrors, tabs, activeTabId, allContentItems]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasAnyUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasAnyUnsavedChanges]);

  // Auto-close drawer on small screens when assistant panel opens
  useEffect(() => {
    if (assistantPanelOpen && window.innerWidth < 1200) {
      setDrawerOpen(false);
    }
  }, [assistantPanelOpen]);

  // Handle editor actions from the assistant
  const handleEditorAction = useCallback((action: EditorAction) => {
    console.log('[EditorPage] handleEditorAction called:', action.action, 'activeTabId:', activeTabId);

    if (action.action === 'update_content') {
      // Update the current tab's content
      if (activeTabId && action.content) {
        console.log('[EditorPage] Updating tab content, length:', action.content.length);
        updateTabContent(activeTabId, action.content);
        toast({
          title: 'Content Updated',
          description: action.message || 'The assistant updated the editor content',
        });
      } else {
        console.warn('[EditorPage] Cannot update: activeTabId=', activeTabId, 'content exists:', !!action.content);
      }
    } else if (action.action === 'open_file') {
      // Open a new file (created by assistant)
      if (action.item_id) {
        console.log('[EditorPage] Opening new file:', action.item_id);
        openTab(action.item_id, {
          name: action.name || 'Untitled',
          contentType: action.content_type || 'agent',
        });
        toast({
          title: 'File Created',
          description: `Created new ${action.content_type}: ${action.name}`,
        });
      }
    }
  }, [activeTabId, updateTabContent, openTab, toast]);

  // Register the editor action handler with the global assistant
  useEffect(() => {
    console.log('[EditorPage] Registering editor action handler');
    registerEditorActionHandler(handleEditorAction);
    return () => {
      console.log('[EditorPage] Unregistering editor action handler');
      registerEditorActionHandler(null);
    };
  }, [registerEditorActionHandler, handleEditorAction]);

  const loadVersions = useCallback(async (itemId: string) => {
    try {
      const [versionsData, itemData] = await Promise.all([
        contentApi.listVersions(itemId),
        contentApi.getItem(itemId),
      ]);
      setVersions(versionsData);
      setPublishedVersionId(itemData?.published_version_id || null);
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  }, []);

  const handleFileSelect = useCallback(
    async (item: ContentItem) => {
      try {
        await openTab(item.id, {
          name: item.name,
          contentType: item.content_type,
        });
      } catch (error) {
        console.error('Failed to open file:', error);
        toast({
          title: 'Error',
          description: 'Failed to open file',
          variant: 'destructive',
        });
      }
    },
    [openTab, toast]
  );

  const handleContentChange = useCallback((newContent: string) => {
    if (activeTabId) {
      updateTabContent(activeTabId, newContent);
    }
  }, [activeTabId, updateTabContent]);

  const handleSaveDraft = useCallback(async () => {
    if (!activeTabId || !activeTab) return;

    setLoading(true);
    try {
      const result = await contentApi.saveDraft(activeTabId, content, 'Saved from editor');
      markTabSaved(activeTabId, content);

      const versionsData = await contentApi.listVersions(activeTabId);
      setVersions(versionsData);

      toast({
        title: 'Saved',
        description: `Saved as draft (v${result.version_number})`,
      });
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to save draft',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [activeTabId, activeTab, content, markTabSaved, toast]);

  const handlePublish = useCallback(async () => {
    if (!activeTabId || !activeTab) return;

    setLoading(true);
    try {
      if (hasUnsavedChanges) {
        await contentApi.saveDraft(activeTabId, content, 'Published from editor');
        markTabSaved(activeTabId, content);
      }

      const result = await contentApi.publishVersion(activeTabId);

      await loadVersions(activeTabId);

      toast({
        title: 'Published',
        description: `Published v${result.version_number}`,
      });
    } catch (error) {
      console.error('Failed to publish:', error);
      toast({
        title: 'Error',
        description: 'Failed to publish',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [activeTabId, activeTab, content, hasUnsavedChanges, markTabSaved, loadVersions, toast]);

  const handleValidate = useCallback(async () => {
    if (!activeTabId) return;

    setLoading(true);
    try {
      const serverErrors = await contentApi.validateContent(activeTabId, content);
      setServerValidationErrors(serverErrors);

      const currentBrowserErrors = browserErrorsRef.current;
      const errorCount =
        currentBrowserErrors.filter((e: ValidationError) => e.severity === 'error' || !e.severity).length +
        serverErrors.filter((e: ValidationError) => e.severity === 'error' || !e.severity).length;
      const warningCount =
        currentBrowserErrors.filter((e: ValidationError) => e.severity === 'warning').length +
        serverErrors.filter((e: ValidationError) => e.severity === 'warning').length;

      if (errorCount === 0 && warningCount === 0) {
        toast({
          title: 'Validation Passed',
          description: 'No errors found',
        });
      } else if (errorCount === 0) {
        toast({
          title: 'Validation Passed',
          description: `No errors found (${warningCount} warning${warningCount !== 1 ? 's' : ''})`,
        });
      } else {
        toast({
          title: 'Validation Issues',
          description: `Found ${errorCount} error${errorCount !== 1 ? 's' : ''}${warningCount > 0 ? ` and ${warningCount} warning${warningCount !== 1 ? 's' : ''}` : ''}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to validate:', error);
      toast({
        title: 'Error',
        description: 'Failed to validate',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [activeTabId, content, toast]);

  const handleCompareVersions = useCallback(
    async (v1: number, v2: number) => {
      if (!activeTabId) return;

      setLoading(true);
      try {
        const [content1, content2] = await Promise.all([
          contentApi.getVersionContent(activeTabId, v1),
          contentApi.getVersionContent(activeTabId, v2),
        ]);

        setDiffOriginal(content1);
        setDiffModified(content2);
        setDiffTitle(`Comparing v${v1} with v${v2}`);
        setDiffViewerOpen(true);
      } catch (error) {
        console.error('Failed to load versions for comparison:', error);
        toast({
          title: 'Error',
          description: 'Failed to compare versions',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [activeTabId, toast]
  );

  const handleRestoreVersion = useCallback(
    async (versionNumber: number) => {
      if (!activeTabId) return;

      if (!window.confirm(`Restore content from v${versionNumber}? This will replace your current content.`)) {
        return;
      }

      setLoading(true);
      try {
        const restoredContent = await contentApi.getVersionContent(activeTabId, versionNumber);
        updateTabContent(activeTabId, restoredContent);
        toast({
          title: 'Restored',
          description: `Restored content from v${versionNumber}`,
        });
      } catch (error) {
        console.error('Failed to restore version:', error);
        toast({
          title: 'Error',
          description: 'Failed to restore version',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [activeTabId, updateTabContent, toast]
  );

  const handleViewVersion = useCallback(
    async (version: Version) => {
      if (!activeTabId || !activeTab) return;

      setLoading(true);
      try {
        const versionContent = await contentApi.getVersionContent(activeTabId, version.version_number);
        openVersionTab(
          activeTabId,
          activeTab.name,
          version.version_number,
          versionContent,
          activeTab.contentType
        );
      } catch (error) {
        console.error('Failed to load version:', error);
        toast({
          title: 'Error',
          description: 'Failed to load version',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [activeTabId, activeTab, openVersionTab, toast]
  );

  // Go back to draft from a version tab
  const handleBackToDraft = useCallback(() => {
    if (!activeTab?.isReadOnly || !activeTab.sourceItemId) return;
    // Switch to the source item's main tab (the editable draft)
    setActiveTab(activeTab.sourceItemId);
  }, [activeTab, setActiveTab]);

  const handleValidationClick = useCallback((error: ValidationError) => {
    console.log('Navigate to validation error:', error);
  }, []);

  // Handle code suggestions from AI assistant
  const handleApplyCode = useCallback((code: string) => {
    if (!activeTabId) return;
    // Replace the entire content with the suggested code
    // In a more advanced version, we could try to intelligently merge
    updateTabContent(activeTabId, code);
    toast({
      title: 'Applied',
      description: 'Code suggestion applied to editor',
    });
  }, [activeTabId, updateTabContent, toast]);

  // Toggle assistant panel (close other panels if opening assistant on small screens)
  const handleToggleAssistant = useCallback(() => {
    if (!assistantPanelOpen) {
      setVersionPanelOpen(false); // Close version panel when opening assistant
      // Close file tree on smaller screens to make room
      if (window.innerWidth < 1200) {
        setDrawerOpen(false);
      }
    }
    toggleAssistantPanel();
  }, [assistantPanelOpen, toggleAssistantPanel]);

  // Toggle version panel
  const handleToggleVersions = useCallback(() => {
    setVersionPanelOpen((prev) => !prev);
  }, []);

  // Run agent in IDE (auto-saves draft first)
  const handleRunInIDE = useCallback(async () => {
    if (!activeTabId || !activeTab) return;

    setLoading(true);
    try {
      // Auto-save draft if there are unsaved changes
      if (hasUnsavedChanges) {
        await contentApi.saveDraft(activeTabId, content, 'Auto-saved before IDE');
        markTabSaved(activeTabId, content);
        toast({
          title: 'Draft Saved',
          description: 'Changes saved before opening IDE',
        });
      }

      // Navigate to IDE with this agent
      navigate(`/developer/ide?item=${activeTabId}`);
    } catch (error) {
      console.error('Failed to save before IDE:', error);
      toast({
        title: 'Error',
        description: 'Failed to save draft',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [activeTabId, activeTab, hasUnsavedChanges, content, markTabSaved, navigate, toast]);

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Secondary Toolbar */}
      <div
        className="fixed top-14 left-0 z-40 bg-background border-b transition-all h-11"
        style={{ right: assistantPanelOpen ? assistantPanelWidth : versionPanelOpen ? 350 : 0 }}
      >
        <div className="flex items-center px-4 h-full">
          <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(!drawerOpen)} className="mr-4">
            <Menu className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold flex-1">
            Content Editor
            {selectedItem && (
              <span className="ml-2 text-muted-foreground font-normal">
                {selectedItem.name}.md
                {hasUnsavedChanges && (
                  <span className="ml-2 text-yellow-500">(unsaved)</span>
                )}
              </span>
            )}
          </h1>
          {loading && <Loader2 className="h-5 w-5 animate-spin mr-2" />}
          {pyodideLoading && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="mr-2">
                  Loading Pyodide
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Loading schema validator...</TooltipContent>
            </Tooltip>
          )}
          {pyodideReady && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="mr-2 border-green-500 text-green-600">
                  Validation Active
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Real-time validation running in browser</TooltipContent>
            </Tooltip>
          )}
          {pyodideError && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="destructive" className="mr-2">
                  Schema Error
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{`Pyodide error: ${pyodideError}`}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {pyodideLoading && <Progress className="h-0.5" />}
      </div>

      {/* File Tree Sidebar */}
      {drawerOpen && (
        <aside className="w-[280px] border-r bg-background mt-11 flex-shrink-0 overflow-hidden">
          <FileTree onFileSelect={handleFileSelect} selectedItemId={selectedItem?.id} />
        </aside>
      )}

      {/* Main Content Area */}
      <main
        className="flex-1 flex flex-col mt-11 transition-all min-w-0"
        style={{ marginRight: versionPanelOpen ? 350 : 0 }}
      >
        {selectedItem ? (
          <>
            <EditorToolbar
              onSave={handleSaveDraft}
              onPublish={handlePublish}
              onValidate={handleValidate}
              onToggleVersions={handleToggleVersions}
              onToggleAssistant={handleToggleAssistant}
              onRunInIDE={handleRunInIDE}
              hasUnsavedChanges={hasUnsavedChanges}
              isPublished={publishedVersionId != null}
              disabled={loading}
              assistantOpen={assistantPanelOpen}
              readOnly={activeTab?.isReadOnly}
              contentType={activeTab?.contentType}
            />

            {/* Tab Bar */}
            <TabBar />

            <div className="flex-1 overflow-hidden">
              <MonacoEditor
                value={content}
                onChange={handleContentChange}
                contentType={activeTab?.contentType}
                validationErrors={validationErrors}
                onOpenReference={openTab}
                readOnly={activeTab?.isReadOnly}
              />
            </div>

            {validationErrors.length > 0 && (
              <ValidationPanel errors={validationErrors} onErrorClick={handleValidationClick} />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p className="text-lg">Select a file from the sidebar to edit</p>
          </div>
        )}
      </main>

      {/* Version History Panel */}
      {versionPanelOpen && activeTabId && (
        <div className="fixed right-0 top-[6.5rem] bottom-0 w-[350px] border-l bg-background">
          <VersionHistory
            versions={versions}
            publishedVersionId={publishedVersionId}
            currentVersionNumber={
              activeTab?.isReadOnly
                ? activeTab.versionNumber
                : versions.length > 0
                  ? Math.max(...versions.map(v => v.version_number))
                  : undefined
            }
            isViewingReadOnlyVersion={activeTab?.isReadOnly}
            onCompare={handleCompareVersions}
            onRestore={handleRestoreVersion}
            onPublish={(versionId) => contentApi.publishVersion(activeTabId, versionId)}
            onViewVersion={handleViewVersion}
            onBackToDraft={handleBackToDraft}
          />
        </div>
      )}

      {/* Diff Viewer Dialog */}
      <DiffViewer
        open={diffViewerOpen}
        onClose={() => setDiffViewerOpen(false)}
        original={diffOriginal}
        modified={diffModified}
        title={diffTitle}
      />
    </div>
  );
}

// Main EditorPage component wraps content with tabs provider
export function EditorPage() {
  return (
    <EditorTabsProvider storageKey="editor_tabs_state">
      <EditorPageContent />
    </EditorTabsProvider>
  );
}

export default EditorPage;
